import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import PointieTypography from "../PointieTypography";
import { trackEvent } from "../../../libraries/firebase";
import { getDownloadUrl } from "../../../data/DownloadButtons";

const STORAGE_KEY = "pointie_install_banner_dismissed_until";
const DISMISS_DAYS = 7;

function isDesktopChromium() {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/Mobile|Android|iPhone|iPad|iPod/i.test(ua)) return false;
  if (!/Chrome|Chromium|Edg|OPR/i.test(ua)) return false;
  if (/Firefox|FxiOS/i.test(ua)) return false;
  return true;
}

function isPointieInstalled() {
  if (typeof document === "undefined") return false;
  return document.documentElement.getAttribute("data-pointie-installed") === "1";
}

function isDismissed() {
  try {
    const until = localStorage.getItem(STORAGE_KEY);
    if (!until) return false;
    return Date.now() < parseInt(until, 10);
  } catch {
    return false;
  }
}

function setDismissed() {
  try {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY, String(until));
  } catch {
    // ignore
  }
}

function InstallBanner() {
  const { t } = useTranslation(["home", "common"]);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isDesktopChromium()) return;
    if (isDismissed()) return;
    const timer = setTimeout(() => {
      if (!isPointieInstalled()) setShow(true);
    }, 800);
    const onVisibility = () => {
      if (isPointieInstalled()) setShow(false);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const handleDismiss = () => {
    setDismissed();
    setShow(false);
    trackEvent("install_banner_dismiss");
  };

  const handleClick = () => {
    trackEvent("install_banner_click");
  };

  if (!show) return null;

  return (
    <Box
      role="region"
      aria-label="Install Pointie banner"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1100,
        background: "linear-gradient(135deg, #1a73e8, #6366f1)",
        color: "#fff",
        px: { xs: 2, md: 3 },
        py: 1.25,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        flexWrap: "wrap",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <span style={{ fontSize: "1.1rem" }}>🎁</span>
        <PointieTypography
          variant="body2"
          color="white"
          sx={{ fontWeight: 500, fontSize: { xs: "0.85rem", md: "0.95rem" } }}
        >
          {t("home:installBanner.message")}
        </PointieTypography>
      </Box>
      <Box
        component="a"
        href={getDownloadUrl()}
        target="_blank"
        rel="noreferrer"
        onClick={handleClick}
        sx={{
          bgcolor: "#fff",
          color: "#1a73e8",
          px: 2,
          py: 0.6,
          borderRadius: 1.5,
          fontWeight: 700,
          fontSize: "0.85rem",
          textDecoration: "none",
          whiteSpace: "nowrap",
          transition: "all 0.15s",
          "&:hover": { bgcolor: "#f1f5f9", transform: "translateY(-1px)" },
        }}
      >
        {t("home:installBanner.cta")}
      </Box>
      <IconButton
        onClick={handleDismiss}
        size="small"
        aria-label="Close install banner"
        sx={{
          color: "rgba(255,255,255,0.85)",
          ml: 0.5,
          "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.15)" },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

export default InstallBanner;
